import { PortalRichText, PortalRichTextPart } from '../../../models/PortalArtigoModel'

export type PortalRichTextDraftPartType = 'text' | 'strong' | 'link'

export type PortalRichTextDraftPart = {
  type: PortalRichTextDraftPartType
  text: string
  href?: string
  target?: '_blank' | '_self'
  rel?: string
  className?: string
}

export function hasRichTextContent(value?: PortalRichText) {
  return getRichTextPlainText(value).trim().length > 0
}

export function getRichTextPlainText(value?: PortalRichText) {
  if (!value) return ''
  const parts = Array.isArray(value) ? value : [value]

  return parts
    .map((part) => {
      if (typeof part === 'string') return part
      if (part.text) return part.text
      return part.type === 'link' ? part.href || '' : ''
    })
    .join('')
}

export function getRichTextListSummary(values: PortalRichText[] = []) {
  const text = values
    .map(getRichTextPlainText)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!text) return 'Parágrafos ainda não preenchidos.'
  return text.length > 180 ? `${text.slice(0, 177)}...` : text
}

export function formatCount(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`
}

export function normalizePortalRichText(value?: PortalRichText): PortalRichText | undefined {
  const normalizedParts = toPortalRichTextDraftParts(value)
    .map((part) => {
      const text = part.text
      const href = part.href?.trim()

      if (!text.trim() && part.type !== 'link') return null
      if (part.type === 'link' && !text.trim() && !href) return null

      return {
        ...part,
        text,
        href,
      }
    })
    .filter(Boolean) as PortalRichTextDraftPart[]

  return toPortalRichText(normalizedParts)
}

export function normalizePortalRichTextList(values?: PortalRichText[]) {
  return (values || [])
    .map((value) => normalizePortalRichText(value))
    .filter(Boolean) as PortalRichText[]
}

export function createPortalRichTextDraftPart(
  type: PortalRichTextDraftPartType
): PortalRichTextDraftPart {
  if (type === 'link') return { type, text: '', href: '' }
  return { type, text: '' }
}

export function toPortalRichTextDraftParts(value?: PortalRichText): PortalRichTextDraftPart[] {
  if (value === undefined || value === null) return []
  const parts = Array.isArray(value) ? value : [value]

  return parts.map((part) => {
    if (typeof part === 'string') {
      return { type: 'text', text: part }
    }

    if (part.type === 'link') {
      return {
        type: 'link',
        text: part.text || '',
        href: part.href || '',
        target: part.target,
        rel: part.rel,
        className: part.className,
      }
    }

    return {
      type: 'strong',
      text: part.text || '',
      className: part.className,
    }
  })
}

export function toPortalRichText(parts: PortalRichTextDraftPart[]): PortalRichText | undefined {
  if (parts.length === 0) return undefined

  const portalParts = parts.map<PortalRichTextPart>((part) => {
    if (part.type === 'text') return part.text
    if (part.type === 'strong') {
      const strongPart: PortalRichTextPart = {
        type: 'strong',
        text: part.text,
      }
      if (part.className) strongPart.className = part.className
      return strongPart
    }

    const linkPart: PortalRichTextPart = {
      type: 'link',
      text: part.text,
      href: part.href || '',
      target: part.target || '_blank',
      rel: part.rel || 'noopener noreferrer',
    }
    if (part.className) linkPart.className = part.className
    return linkPart
  })

  if (portalParts.length === 1) return portalParts[0]
  return portalParts
}
